import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Quote, Star } from "lucide-react";

const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const textTestimonials = [
    {
      quote:
        "L'accompagnement de Gilles et la communauté engagée ont été des éléments déterminants pour ma réussite. La Tribu est toujours là pour offrir des conseils et du soutien. Plus on avance plus on prend la mesure de l'expertise et des connaissances de Gilles. C'est assez bluffant j'avoue d'expérimenter des outils novateurs qui bougent les codes et surtout fonctionnent de manière implacable et assez radicale. Un réel changement de paradigme.",
      name: "Sophie R.",
      role: "Psychopraticienne",
      image: "https://gillesdelieuze.com/wp-content/uploads/2023/07/testimonial-2.png",
    },
    {
      quote:
        "Depuis que j'ai suivi la formation Quantic Revolution, mes consultations ont pris une nouvelle dimension. Je suis désormais capable d'agir sur tous les plans de l'être, et mes patients ressentent des transformations profondes et durables.",
      name: "Marie L.",
      role: "Thérapeute Énergétique",
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/04/bb-hwc-single-page-testimonial3-1.jpg",
    },
    {
      quote:
        "Cette formation m'a apporté des outils puissants et une compréhension systémique de l'humain. Mon approche est plus ajustée, ce qui a considérablement amélioré les résultats de mes clients en ouvrant le champ des possibles à de nouvelles réalités.",
      name: "Jean P.",
      role: "Coach en Développement Personnel et Sportif",
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/03/hero-contact-us.png",
    },
  ];

  const videoTestimonials = [
    { id: "kgssRVispg8", name: "Gaëlle Macera" },
    { id: "1g2p6VBBqm8", name: "Elisa Demanet" },
    { id: "DXJ-vXPwXEY", name: "Ophélie" },
    { id: "g-KkG849cBM", name: "Julien" },
  ];

  return (
    <section
      ref={ref}
      data-testid="testimonials-section"
      className="relative py-24 lg:py-32 overflow-hidden"
      style={{ background: "linear-gradient(180deg, #030014 0%, #090920 50%, #030014 100%)" }}
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-cyan-400 text-sm uppercase tracking-[0.2em] mb-4 block">
            Témoignages
          </span>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-white mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Ils ont vécu la <span className="text-gradient-cosmic">Transformation</span>
          </h2>
        </motion.div>

        {/* Text Testimonials */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {textTestimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              data-testid={`testimonial-card-${index}`}
              className="testimonial-card group"
            >
              {/* Quote Icon */}
              <div className="mb-4">
                <Quote className="w-8 h-8 text-purple-500/50" />
              </div>

              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>

              {/* Quote Text */}
              <p className="text-slate-300 text-sm leading-relaxed mb-6 line-clamp-6 group-hover:line-clamp-none transition-all">
                "{testimonial.quote}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-4 mt-auto">
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-purple-500/30">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <div className="text-white font-medium">{testimonial.name}</div>
                  <div className="text-cyan-400 text-sm">{testimonial.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Video Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h3
            className="text-2xl sm:text-3xl font-light text-white text-center mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Témoignages en <span className="text-gradient-cosmic">Vidéo</span>
          </h3>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {videoTestimonials.map((video, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                data-testid={`video-testimonial-${index}`}
                className="video-wrapper"
              >
                <div className="youtube-container">
                  <iframe
                    src={`https://www.youtube.com/embed/${video.id}?rel=0`}
                    title={`Témoignage ${video.name}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="p-4">
                  <p className="text-white text-sm font-medium">Témoignage de {video.name}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
